import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  BooleanField,
  NumberField,
  EditButton,
  DeleteButton,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  BooleanInput,
  required,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  Filter,
  SearchInput,
  SimpleList,
  FunctionField,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material';

// FAQ 카테고리 선택 옵션
const FAQ_CATEGORIES = [
  { id: 'GENERAL', name: '일반' },
  { id: 'DELIVERY', name: '배송' },
  { id: 'ORDER', name: '주문' },
  { id: 'PAYMENT', name: '결제' },
  { id: 'RETURN', name: '교환/반품' },
  { id: 'PRODUCT', name: '상품' },
  { id: 'MEMBER', name: '회원' },
];

// 카테고리 라벨 매핑
const CATEGORY_LABELS: Record<string, string> = {
  GENERAL: '일반',
  DELIVERY: '배송',
  ORDER: '주문',
  PAYMENT: '결제',
  RETURN: '교환/반품',
  PRODUCT: '상품',
  MEMBER: '회원',
};

// 카테고리 색상 매핑
const CATEGORY_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default'> = {
  GENERAL: 'default',
  DELIVERY: 'info',
  ORDER: 'primary',
  PAYMENT: 'success',
  RETURN: 'warning',
  PRODUCT: 'secondary',
  MEMBER: 'default',
};

// 카테고리 필드 컴포넌트
const CategoryField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const category = record.category;
  const label = CATEGORY_LABELS[category] || category;
  const color = CATEGORY_COLORS[category] || 'default';

  return (
    <Chip
      label={label}
      color={color}
      size="small"
      variant="outlined"
    />
  );
};

// 필터
const FAQFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn placeholder="질문 검색" />
    <SelectInput source="category" label="카테고리" choices={FAQ_CATEGORIES} />
    <BooleanInput source="isActive" label="활성화" />
  </Filter>
);

// 목록 액션
const ListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

// FAQ 목록
export const FAQList = () => {
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E' }}>
          자주 묻는 질문
        </Typography>
      </Box>
      <List
        title="자주 묻는 질문"
        filters={<FAQFilter />}
        actions={<ListActions />}
        sort={{ field: 'displayOrder', order: 'ASC' }}
        perPage={25}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => record.question}
            secondaryText={(record) => (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                <Chip
                  label={CATEGORY_LABELS[record.category] || record.category}
                  size="small"
                  variant="outlined"
                />
                {!record.isActive && (
                  <Chip label="비활성" size="small" color="error" variant="outlined" />
                )}
              </Box>
            )}
            linkType="edit"
          />
        ) : (
          <Datagrid rowClick="edit">
            <NumberField source="displayOrder" label="순서" />
            <FunctionField
              label="카테고리"
              render={() => <CategoryField />}
            />
            <FunctionField
              label="질문"
              render={(record: any) => (
                <Typography
                  variant="body2"
                  sx={{
                    maxWidth: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {record.question}
                </Typography>
              )}
            />
            <BooleanField source="isActive" label="활성화" />
            <DateField source="createdAt" label="작성일" />
            <EditButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// FAQ 생성
export const FAQCreate = () => (
  <Create title="FAQ 등록" redirect="list">
    <SimpleForm>
      <Box sx={{ width: '100%', maxWidth: 800 }}>
        <SelectInput
          source="category"
          label="카테고리"
          choices={FAQ_CATEGORIES}
          defaultValue="GENERAL"
          validate={required()}
          fullWidth
        />
        <TextInput
          source="question"
          label="질문"
          validate={required()}
          fullWidth
          multiline
          rows={2}
        />
        <TextInput
          source="answer"
          label="답변"
          validate={required()}
          fullWidth
          multiline
          rows={6}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <NumberInput
            source="displayOrder"
            label="표시 순서"
            defaultValue={0}
            min={0}
          />
          <BooleanInput
            source="isActive"
            label="활성화"
            defaultValue={true}
          />
        </Box>
      </Box>
    </SimpleForm>
  </Create>
);

// FAQ 수정
export const FAQEdit = () => (
  <Edit title="FAQ 수정">
    <SimpleForm>
      <Box sx={{ width: '100%', maxWidth: 800 }}>
        <SelectInput
          source="category"
          label="카테고리"
          choices={FAQ_CATEGORIES}
          validate={required()}
          fullWidth
        />
        <TextInput
          source="question"
          label="질문"
          validate={required()}
          fullWidth
          multiline
          rows={2}
        />
        <TextInput
          source="answer"
          label="답변"
          validate={required()}
          fullWidth
          multiline
          rows={6}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <NumberInput
            source="displayOrder"
            label="표시 순서"
            min={0}
          />
          <BooleanInput
            source="isActive"
            label="활성화"
          />
        </Box>
      </Box>
    </SimpleForm>
  </Edit>
);
